import React, { useState, useEffect } from 'react';
import { STRRoom } from '../types';
import { API_BASE_URL } from '../../config';
import { useResponsive } from '../../hooks/useResponsive';

interface Props {
  room: STRRoom;
  token: string;
  onClose: () => void;
  onUpdated: () => void;
}

export default function CoManageModal({ room, token, onClose, onUpdated }: Props) {
  const { isMobile } = useResponsive();
  const [email, setEmail] = useState('');
  const [lookupResult, setLookupResult] = useState<{ id: string; name: string; email: string } | null>(null);
  const [collaborators, setCollaborators] = useState(room.collaborators || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    setCollaborators(room.collaborators || []);
  }, [room]);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const handleLookup = async () => {
    if (!email.trim()) return;
    setError('');
    setLookupResult(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-users/lookup?email=${encodeURIComponent(email.trim())}`, {
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail); setLoading(false); return; }
      setLookupResult(data);
    } catch { setError('서버 연결 실패'); }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!lookupResult) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/collaborators`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ user_id: lookupResult.id }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail); setLoading(false); return; }
      setMessage(data.message);
      setCollaborators(prev => [...prev, lookupResult]);
      setLookupResult(null);
      setEmail('');
      onUpdated();
    } catch { setError('서버 연결 실패'); }
    setLoading(false);
  };

  const handleRemove = async (userId: string) => {
    if (!window.confirm('공동관리자를 제거하시겠습니까?')) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/str-rooms/${room.id}/collaborators/${userId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail);
        return;
      }
      setCollaborators(prev => prev.filter(c => c.id !== userId));
      onUpdated();
    } catch { setError('서버 연결 실패'); }
  };

  return (
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{ background: '#fff', borderRadius: 12, padding: isMobile ? 18 : 24, width: isMobile ? 'calc(100vw - 24px)' : 400, maxWidth: 400, maxHeight: '80vh', overflowY: 'auto' }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 2px' }}>공동관리자</h3>
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 16px' }}>{room.name}</p>

        {/* 기존 공동관리자 목록 */}
        {collaborators.length > 0 ? (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 12, color: '#666', fontWeight: 600, marginBottom: 6 }}>
              현재 공동관리자 ({collaborators.length}명)
            </p>
            {collaborators.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#f8f9fa', borderRadius: 8, marginBottom: 6 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</span>
                  <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>{c.email}</span>
                </div>
                <button
                  onClick={() => handleRemove(c.id)}
                  style={{ fontSize: 11, color: '#e53935', background: 'none', border: '1px solid #e53935', borderRadius: 4, padding: '3px 10px', cursor: 'pointer' }}
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 12, color: '#aaa', marginBottom: 16, padding: '10px', background: '#f8f9fa', borderRadius: 8, textAlign: 'center' }}>
            등록된 공동관리자가 없습니다
          </p>
        )}

        {/* 새 공동관리자 추가 */}
        <p style={{ fontSize: 12, color: '#666', fontWeight: 600, marginBottom: 6 }}>공동관리자 추가</p>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <input
            type="email"
            placeholder="이메일 주소 입력"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setLookupResult(null); setError(''); setMessage(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            style={{ flex: 1, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
          />
          <button
            onClick={handleLookup}
            disabled={loading}
            style={{ padding: '8px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            조회
          </button>
        </div>

        {/* 사용자 확인 */}
        {lookupResult && (
          <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 12, marginBottom: 8 }}>
            <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px' }}>
              '{lookupResult.name}' 맞습니까?
            </p>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={handleAdd}
                disabled={loading}
                style={{ padding: '6px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
              >
                확인
              </button>
              <button
                onClick={() => { setLookupResult(null); setEmail(''); }}
                style={{ padding: '6px 16px', background: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
              >
                취소
              </button>
            </div>
          </div>
        )}

        {error && <p style={{ fontSize: 12, color: '#e53935', margin: '4px 0' }}>{error}</p>}
        {message && <p style={{ fontSize: 12, color: '#16a34a', margin: '4px 0' }}>{message}</p>}

        {/* 닫기 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', background: '#f5f5f5', border: '1px solid #e2e5e9', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#333' }}
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
